// Cliente WHEP (WebRTC-HTTP Egress Protocol) para consumir el feed en vivo de
// MediaMTX (relay). Portado de packages/relay-transcriber/frontend/src/api/monitor.ts.
//
// Flujo WHEP no-trickle: juntamos los candidatos ICE antes de mandar la oferta
// SDP por POST. MediaMTX responde con la answer y la media fluye por WebRTC.
// MediaMTX emite `Access-Control-Allow-Origin: *` en los endpoints WHEP, así que
// el POST cross-origin desde braindogv2 funciona sin proxy.

/**
 * Conecta a un endpoint WHEP y enchufa el stream recibido al <video>.
 * Devuelve el RTCPeerConnection para poder cerrarlo al cambiar de canal.
 */
export async function startWhep(
  whepUrl: string,
  video: HTMLVideoElement,
  signal?: AbortSignal,
): Promise<RTCPeerConnection> {
  const pc = new RTCPeerConnection();

  // Cualquier fallo (fetch abortado/red/CORS, 404 on-demand, setRemote…) debe
  // cerrar el pc antes de propagar: si no, cada RTCPeerConnection queda colgado
  // y el navegador acaba lanzando "Cannot create so many PeerConnections".
  try {
    pc.addTransceiver("video", { direction: "recvonly" });
    pc.addTransceiver("audio", { direction: "recvonly" });

    const stream = new MediaStream();
    pc.ontrack = (e) => {
      stream.addTrack(e.track);
      video.srcObject = stream;
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitForIceGathering(pc);

    const res = await fetch(whepUrl, {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      body: pc.localDescription?.sdp ?? offer.sdp,
      signal,
    });
    if (!res.ok) {
      // MediaMTX devuelve 404 cuando el path on-demand aún no tiene señal
      // (recién spawnea el ffmpeg con el primer WHEP). El llamador reintenta.
      throw new Error(`WHEP ${res.status} ${res.statusText}`);
    }

    const answerSdp = await res.text();
    await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
    return pc;
  } catch (err) {
    pc.close();
    throw err;
  }
}

function waitForIceGathering(pc: RTCPeerConnection, timeoutMs = 2000): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener("icegatheringstatechange", check);
      resolve();
    };
    const check = () => {
      if (pc.iceGatheringState === "complete") done();
    };
    pc.addEventListener("icegatheringstatechange", check);
    // Fallback: no esperar para siempre si algún candidato se cuelga.
    setTimeout(done, timeoutMs);
  });
}
